import { useCallback, useRef, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { HistoryReducer } from "shared/redux/reducers/history/HistoryReducer";
import { CartReducer } from "shared/redux/reducers/shopping/CartReducer";
import { executeSendOut } from "shared/utils/nativeBridge";
import NiceModal from "@ebay/nice-modal-react";


/**
 * 배출 프로세스를 관리하는 커스텀 훅
 * @param {Function} sendMessageFn 중복 방지 메시지 전송 함수
 * @param {Function} getCurrentDeviceCode 현재 디바이스 코드 가져오는 함수
 * @param {Function} logEvent 로깅 함수 (선택적)
 * @returns {Object} 배출 관련 상태와 함수들
 */
export function useDispenseManager(sendMessageFn, getCurrentDeviceCode, logEvent = console.log) {
    const dispatch = useDispatch();
    const [isRailActive, setIsRailActive] = useState(false);
    const [activeDevice, setActiveDevice] = useState(null);
    const [requestDeviceCode, setRequestDeviceCode] = useState(null);
    const [activeOutlets, setActiveOutlets] = useState([]);
    const lastDispenseRequestRef = useRef(null);
    const componentMountedRef = useRef(true);
    const railCompletionTimeoutRef = useRef(null);
    const notifyRailCompletedRef = useRef(null);

    // 주문 ID 관리 개선
    const orderKeyRef = useRef(null);
    const generateOrderKey = useCallback(() => {
        return `order_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
    }, []);

    // 컴포넌트 언마운트 감지
    useEffect(() => {
        // 마운트 시
        componentMountedRef.current = true;

        // 언마운트 시 정리
        return () => {
            componentMountedRef.current = false;

            // 지연 초기화 타이머 정리
            if (railCompletionTimeoutRef.current) {
                clearTimeout(railCompletionTimeoutRef.current);
                railCompletionTimeoutRef.current = null;
            }
        };
    }, []);



    // notifyRailCompleted 함수 업데이트
    useEffect(() => {
        notifyRailCompletedRef.current = () => {
            if (!componentMountedRef.current) return;

            const deviceCode = getCurrentDeviceCode();
            if (!deviceCode) {
                logEvent("레일 완료 알림 오류", "디바이스 코드를 가져올 수 없음");
                return;
            }

            logEvent("레일 완료 알림", `레일 작업 완료 알림 전송: ${deviceCode}`);

            //완료했으니 redux에서 상태 변화
            dispatch(HistoryReducer.actions.setIsRailActive({
                isRailActive: false,
                deviceCode: deviceCode
              }));

            // 배출 상태 정보 포함
            sendMessageFn({
                code: "railCompleted",
                deviceCode,
                content: {
                    deviceCode,
                    requestDeviceCode: requestDeviceCode || deviceCode,
                    timestamp: Date.now(),
                    orderKey: orderKeyRef.current
                }
            }).catch(error => {
                if (!componentMountedRef.current) return;

                logEvent("레일 완료 알림 오류", `레일 완료 알림 전송 실패: ${error.message}`);

                // 오류 발생 시 재시도 (5초 후)
                setTimeout(() => {
                    if (!componentMountedRef.current) return;

                    logEvent("레일 완료 알림 재시도", `${deviceCode}에 대한 알림 재시도 중`);
                    sendMessageFn({
                        code: "railCompleted",
                        deviceCode,
                        content: {
                            deviceCode,
                            requestDeviceCode: requestDeviceCode || deviceCode,
                            timestamp: Date.now(),
                            orderKey: orderKeyRef.current,
                            retry: true
                        }
                    }).catch(retryError => {
                        if (!componentMountedRef.current) return;
                        logEvent("레일 완료 알림 재시도 실패", `재시도 실패: ${retryError.message}`);
                    });
                }, 5000);
            });
        };
    }, [getCurrentDeviceCode, sendMessageFn, logEvent, requestDeviceCode]);

    // 배출 명령 실행 핸들러
    const handleExecuteDispense = useCallback((payloadData) => {
        try {
            const currentDeviceCode = getCurrentDeviceCode();
            if (!currentDeviceCode) {
                throw new Error("디바이스 코드를 가져올 수 없음");
            }

            // 새 주문 키 생성
            const orderKey = payloadData.orderKey || generateOrderKey();
            orderKeyRef.current = orderKey;

            // 통합 명령 데이터 구조 확인
            const allKiosks = payloadData.content.allKiosks || {};
            const allPortMaps = payloadData.content.allPortMaps || {};
            const originalDeviceCode = payloadData.content.originalDeviceCode;

            logEvent("통합 배출 명령 수신", `원본 디바이스: ${originalDeviceCode}, 현재 디바이스: ${currentDeviceCode}, 주문 키: ${orderKey}`);

            const allOutlets = Object.values(allKiosks)
                .flatMap(devices =>
                    devices
                        .filter(device => device.outletNumber !== undefined)
                        .map(device => device.outletNumber)
                )
                .sort((a, b) => a - b);

            // 현재 키오스크용 데이터 검사
            const myDevices = allKiosks[currentDeviceCode];

            // 상태 초기화 - Redux 사용
            dispatch(HistoryReducer.actions.resetOutletState());

            // 현재 디바이스가 원본 요청자인 경우, 모든 키오스크의 배출구 정보를 가져옴
            if (currentDeviceCode === originalDeviceCode) {
                dispatch(HistoryReducer.actions.setActiveOutlets(allOutlets));

                // 모든 키오스크의 모든 디바이스에서 배출구 정보 수집
                dispatch(HistoryReducer.actions.setRailStatus({
                    isRailActive: true,
                    deviceCode: currentDeviceCode,
                    requestDeviceCode: originalDeviceCode
                }));

                // 중복 제거 및 정렬
                const uniqueAllOutlets = [...new Set(allOutlets)].sort((a, b) => a - b);

                if (uniqueAllOutlets.length > 0) {
                    logEvent("원본 요청자로서 모든 배출구 정보 수집", uniqueAllOutlets.join(', '));

                    // Redux 상태 업데이트
                    dispatch(HistoryReducer.actions.setActiveOutlets(uniqueAllOutlets));

                    // 레일 상태 업데이트
                    dispatch(HistoryReducer.actions.setRailStatus({
                        isRailActive: true,
                        deviceCode: currentDeviceCode,
                        requestDeviceCode: originalDeviceCode
                    }));

                    // 로컬 상태 업데이트
                    setIsRailActive(true);
                    setActiveDevice(currentDeviceCode);
                    setRequestDeviceCode(originalDeviceCode);
                    setActiveOutlets(uniqueAllOutlets);
                }
            }
            // 다른 키오스크의 레일 요청 처리
            else {
                logEvent("다른 키오스크의 레일 요청 처리", `원본=${originalDeviceCode}, 실행=${currentDeviceCode}`);

                // 레일 상태 업데이트
                dispatch(HistoryReducer.actions.setRailStatus({
                    isRailActive: true,
                    deviceCode: currentDeviceCode,
                    requestDeviceCode: originalDeviceCode
                }));

                // 로컬 상태 업데이트
                setIsRailActive(true);
                setActiveDevice(currentDeviceCode);
                setRequestDeviceCode(originalDeviceCode);
            }

            // 현재 디바이스용 데이터가 있는 경우에만 처리
            if (myDevices && myDevices.length > 0) {
                logEvent("배출 명령 처리", `디바이스: ${currentDeviceCode}, 배출 항목 수: ${myDevices.length}`);

                dispatch(HistoryReducer.actions.setRailStatus({
                    isRailActive: true,
                    deviceCode: currentDeviceCode,
                    requestDeviceCode: originalDeviceCode
                }));

                // outletNumber가 없는 경우 대체 로직
                if (allOutlets.length === 0) {
                    logEvent("배출구 번호 누락", "백엔드에서 제공된 배출구 번호가 없습니다. 대체 로직 사용");

                    // 대체 로직으로 배출구 번호 생성
                    const alternativeOutlets = myDevices.map((device, index) => {
                        // 1. deviceId에서 숫자 추출 시도
                        if (device.deviceId) {
                            const match = device.deviceId.match(/(\d+)/);
                            if (match && match[1]) {
                                const num = parseInt(match[1], 10);
                                // 숫자가 1-6 범위 내인지 확인
                                if (num >= 1 && num <= 6) {
                                    return num;
                                }
                            }
                        }

                        // 2. 포트 정보 사용
                        const portMap = {
                            'ttyS3': 1,
                            'ttyS4': 2,
                            'ttyS7': 3,
                            'ttyS1': 4
                        };

                        if (allPortMaps && allPortMaps[currentDeviceCode] && device.deviceId) {
                            const port = allPortMaps[currentDeviceCode][device.deviceId];
                            if (port && portMap[port]) {
                                return portMap[port];
                            }
                        }

                        // 3. 인덱스 + 1 반환
                        return (index % 6) + 1;
                    });

                    // 중복 제거 및 정렬
                    const uniqueAlternativeOutlets = [...new Set(alternativeOutlets)].sort((a, b) => a - b);
                    logEvent("대체 배출구 번호", uniqueAlternativeOutlets.join(', '));

                    // 배출구 정보 저장 (로컬 전용)
                    const combinedOutlets = uniqueAlternativeOutlets;

                    // 활성 배출구 상태 업데이트 (대체 번호 사용)
                    setActiveOutlets(combinedOutlets);

                    // Redux 상태 업데이트
                    dispatch(HistoryReducer.actions.setActiveOutlets(combinedOutlets));
                } else {
                    // 중복 제거 및 정렬 (백엔드 번호 사용)
                    const uniqueOutlets = [...new Set(allOutlets)].sort((a, b) => a - b);
                    logEvent("백엔드 배출구 번호", uniqueOutlets.join(', '));

                    // Redux 상태 업데이트
                    dispatch(HistoryReducer.actions.setActiveOutlets(uniqueOutlets));

                    // 활성 배출구 상태 업데이트
                    setActiveOutlets(uniqueOutlets);
                }

                // 현재 키오스크용 데이터만 추출하여 처리
                const content = myDevices;
                const portMap = allPortMaps[currentDeviceCode] || {};

                // 컨텐츠와 포트맵을 JSON 문자열로 변환 후 base64 인코딩
                const jsonString = JSON.stringify(content);
                const base64EncodedString = btoa(jsonString);

                const portMapString = JSON.stringify(portMap);
                const base64PortMap = btoa(portMapString);

                // 디바이스 배출 실행 (네이티브 브릿지 유틸리티 사용)
                const dispenseResult = executeSendOut(base64EncodedString, base64PortMap);

                if (dispenseResult) {
                    logEvent("배출 실행", `디바이스: ${currentDeviceCode}, 항목 수: ${content.length}, 원본 요청: ${originalDeviceCode}`);
                    logEvent("배출 명령 전송", "배출 명령 전송 완료");

                    try {
                        // 커스텀 이벤트 발생
                        const dispenseEvent = new CustomEvent('dispenseExecuted', {
                            detail: {
                                outletNumbers: allOutlets.length > 0 ? allOutlets : [],
                                deviceCode: currentDeviceCode,
                                requestDeviceCode: originalDeviceCode,
                                orderKey: orderKey
                            }
                        });
                        window.dispatchEvent(dispenseEvent);
                    } catch (e) {
                        logEvent("이벤트 발생 오류", e.message);
                    }
                } else {
                    // 네이티브 브릿지 함수 실행 실패 시
                    sendMessageFn({
                        code: "dispenseResult",
                        success: false,
                        deviceCode: currentDeviceCode,
                        originalDeviceCode: originalDeviceCode,
                        details: "executeSendOut 메소드를 실행할 수 없음"
                    });
                }
            }
        } catch (error) {
            // 오류 결과 전송
            sendMessageFn({
                code: "dispenseResult",
                success: false,
                deviceCode: getCurrentDeviceCode() || "unknown",
                details: error.message
            });

            logEvent("배출 명령 실행 오류", error.message);
        }
    }, [dispatch, getCurrentDeviceCode, logEvent, sendMessageFn, generateOrderKey]);

    // 메시지 핸들러 - 레일 상태 업데이트
    const handleUpdateRailStatus = useCallback((payloadData) => {
        const isRailActiveNew = payloadData.content.railActive === true;
        const deviceCode = payloadData.content.deviceCode;
        const reqDeviceCode = payloadData.content.requestDeviceCode;

        logEvent("레일 상태 업데이트", JSON.stringify(payloadData.content));

        // 이전 상태와 현재 상태 비교를 위한 스냅샷 생성
        const prevState = {
            isRailActive: isRailActive,
            deviceCode: activeDevice,
            requestDeviceCode: requestDeviceCode
        };

        const newState = {
            isRailActive: isRailActiveNew,
            deviceCode: deviceCode,
            requestDeviceCode: reqDeviceCode
        };

        // 의미 있는 변경이 있을 때만 로그 및 상태 업데이트
        const hasChanged =
            prevState.isRailActive !== newState.isRailActive ||
            prevState.deviceCode !== newState.deviceCode ||
            prevState.requestDeviceCode !== newState.requestDeviceCode;

        if (hasChanged) {
            logEvent("레일 상태 변경 감지",
                `old: {active:${prevState.isRailActive}, device:${prevState.deviceCode}, req:${prevState.requestDeviceCode}}` +
                ` -> new: {active:${newState.isRailActive}, device:${newState.deviceCode}, req:${newState.requestDeviceCode}}`);

            // Redux 상태 업데이트
            dispatch(HistoryReducer.actions.setRailStatus(newState));

            // 로컬 상태 업데이트
            setIsRailActive(newState.isRailActive);
            setActiveDevice(newState.deviceCode);
            setRequestDeviceCode(newState.requestDeviceCode);

            // railActive가 false로 바뀐 경우, 완료 확인 로직 추가
            if (prevState.isRailActive && !newState.isRailActive) {
                logEvent("레일 작업 완료 감지", `디바이스: ${newState.deviceCode}`);

                // 현재 디바이스의 요청인 경우
                if (newState.deviceCode === getCurrentDeviceCode()) {
                    logEvent("내 디바이스 요청 완료", `디바이스: ${newState.deviceCode}`);

                    // 커스텀 이벤트 발생 
                    try {
                        const event = new CustomEvent('railDeactivated', {
                            detail: {
                                deviceCode: newState.deviceCode,
                                orderKey: orderKeyRef.current
                            }
                        });
                        window.dispatchEvent(event);
                    } catch (e) {
                        logEvent("이벤트 발생 오류", e.message);
                    }
                }
            }
        } else {
            logEvent("무시된 레일 상태 업데이트", "변경 사항 없음");
        }
    }, [activeDevice, dispatch, getCurrentDeviceCode, isRailActive, logEvent, requestDeviceCode]);

    // 레일 완료 확인 핸들러
    const handleRailCompletionConfirmed = useCallback((payloadData) => {
        logEvent("레일 완료 확인", JSON.stringify(payloadData.content));

        const deviceCode = payloadData.content?.deviceCode;
        const reqDeviceCode = payloadData.content?.requestDeviceCode || deviceCode;
        const orderKeyFromMessage = payloadData.content?.orderKey;
        const currentDeviceCode = getCurrentDeviceCode();

        // 현재 활성화된 배출구 정보 복사
        const currentActiveOutlets = [...activeOutlets];

        // 완료 이벤트 전파
        try {
            // 커스텀 이벤트 생성 및 발생
            const evt = new CustomEvent('railComplete', {
                detail: {
                    deviceCode: deviceCode,
                    requestDeviceCode: reqDeviceCode,
                    timestamp: payloadData.content?.timestamp,
                    activeOutlets: currentActiveOutlets, // 현재 활성 배출구 정보 포함
                    orderKey: orderKeyFromMessage || orderKeyRef.current
                }
            });

            // 이벤트 발생
            window.dispatchEvent(evt);

            logEvent("레일 완료 이벤트 발생", `디바이스: ${deviceCode}, 요청: ${reqDeviceCode}`);
        } catch (e) {
            logEvent("이벤트 발생 오류", e.message);
        }

        // 항상 비활성화 상태로 명시적 업데이트
        dispatch(HistoryReducer.actions.setRailStatus({
            isRailActive: false,
            deviceCode: deviceCode,
            requestDeviceCode: reqDeviceCode
        }));

        // 로컬 상태 업데이트
        setIsRailActive(false);
        setActiveDevice(null);
        setRequestDeviceCode(null);
    }, [dispatch, getCurrentDeviceCode, logEvent, activeOutlets]);

    // 주문 요청 처리
    const handleOrder = useCallback(async (payloadData, navigate, id) => {
        logEvent("주문 메시지 수신", JSON.stringify(payloadData));

        // 내 디바이스의 주문인지 확인
        const currentDeviceCode = getCurrentDeviceCode();
        const orderDeviceCode = payloadData.deviceCode;
        const isMyDeviceOrder = payloadData.deviceCode === currentDeviceCode;

        logEvent('디버깅 상세 정보', JSON.stringify({
            currentDeviceCode,
            orderDeviceCode,
            isEqual: currentDeviceCode === orderDeviceCode,
            orderMessage: JSON.stringify(payloadData)
        }));

        // UI 업데이트 - 내 디바이스 주문일 때만 화면 전환
        if (isMyDeviceOrder) {
            // 새 주문 키 생성
            const orderKey = generateOrderKey();
            orderKeyRef.current = orderKey;

            // 마지막 처리 요청 저장
            lastDispenseRequestRef.current = {
                deviceCode: currentDeviceCode,
                content: payloadData.content,
                timestamp: Date.now(),
                orderKey
            };

            logEvent('내 디바이스 주문 처리', JSON.stringify({
                roomId: id,
                content: payloadData.content,
                orderKey
            }));

            // 경로 이동
            navigate(`/ws/${id}/main/receiveItem`);
            dispatch(CartReducer.actions.reset());

            // 명시적으로 주문 처리 시작 상태 설정
            dispatch(HistoryReducer.actions.setRailStatus({
                isRailActive: false, // 아직 배출 명령 전송 전이므로 false
                deviceCode: currentDeviceCode,
                requestDeviceCode: currentDeviceCode
            }));

            // 로컬 상태 업데이트
            setIsRailActive(false);
            setActiveDevice(currentDeviceCode);
            setRequestDeviceCode(currentDeviceCode);

            // 백엔드로 디스펜스 요청 전달
            const dispenseMessage = {
                code: "dispense",
                roomId: id,
                content: payloadData.content,
                deviceCode: payloadData.deviceCode,
                orderTimestamp: Date.now(), // 주문 시간 추가
                orderKey // 주문 키 추가
            };

            logEvent("배출 요청 메시지 전송", JSON.stringify(dispenseMessage));
            try {
                await sendMessageFn(dispenseMessage);
            } catch (error) {
                logEvent("배출 요청 메시지 전송 중 오류", error.message);

                // 오류 발생 시 3초 후 재시도
                setTimeout(async () => {
                    if (!componentMountedRef.current) return;

                    logEvent("배출 요청 메시지 재시도", "3초 후 재시도");
                    try {
                        await sendMessageFn({
                            ...dispenseMessage,
                            retry: true
                        });
                    } catch (retryError) {
                        logEvent("배출 요청 메시지 재시도 실패", retryError.message);
                    }
                }, 3000);
            }
        } else {
            // 다른 디바이스 주문 처리 - 로깅만
            logEvent("다른 디바이스 주문 감지", `디바이스: ${payloadData.deviceCode}`);
        }
    }, [dispatch, getCurrentDeviceCode, logEvent, sendMessageFn, generateOrderKey]);

    // 메시지 핸들러 - 부분 완료
    const handlePartialRailCompletion = useCallback((payloadData) => {
        logEvent("부분 레일 완료", JSON.stringify(payloadData.content));

        // 부분 완료 메시지 처리 - 필요에 따라 UI 업데이트 가능
        const completedDeviceCode = payloadData.content?.deviceCode;
        const currentDeviceCode = getCurrentDeviceCode();

        // 현재 사용자의 디바이스 코드와 일치하는 경우에만 처리
        if (completedDeviceCode === currentDeviceCode) {
            logEvent("현재 디바이스 부분 완료", `디바이스: ${completedDeviceCode}`);
            // 추가 UI 업데이트 로직이 필요하면 여기에 구현
        }
    }, [getCurrentDeviceCode, logEvent]);

    // 메시지 핸들러 - 배출 오류
    const handleDispenseError = useCallback((payloadData) => {
        //console.error("배출 오류:", payloadData.content);
        logEvent("배출 오류", JSON.stringify(payloadData.content));
        // 사용자에게 오류 알림 표시 로직 구현
        setActiveOutlets([]); // 오류 발생 시 배출구 상태 초기화
    }, [logEvent]);

    // 메시지 핸들러 - 배출 타임아웃
    const handleDispenseTimeout = useCallback((payloadData) => {
        const timeoutDeviceCode = payloadData.content?.deviceCode;
        //console.warn("배출 작업 타임아웃 발생", `디바이스: ${timeoutDeviceCode}`);
        const currentDeviceCode = getCurrentDeviceCode();

        // 내 디바이스의 타임아웃인지 확인
        if (timeoutDeviceCode === currentDeviceCode) {
            // 내 디바이스의 타임아웃
            logEvent("내 디바이스의 타임아웃 발생", `디바이스: ${timeoutDeviceCode}`);

            // 레일 상태 업데이트
            dispatch(HistoryReducer.actions.setRailStatus({
                isRailActive: false,
                deviceCode: currentDeviceCode,
                requestDeviceCode: requestDeviceCode
            }));

            // 로컬 상태 업데이트
            setIsRailActive(false);
            setActiveDevice(null);
            setRequestDeviceCode(null);
            setActiveOutlets([]); // 타임아웃 시 배출구 상태 초기화

            // 추가 UI 업데이트 로직
        } else {
            // 다른 디바이스의 타임아웃 - 상태 초기화
            logEvent("다른 디바이스의 타임아웃 발생", `디바이스: ${timeoutDeviceCode}`);

            dispatch(HistoryReducer.actions.setRailStatus({
                isRailActive: false,
                deviceCode: null,
                requestDeviceCode: null
            }));
        }
    }, [dispatch, getCurrentDeviceCode, logEvent, requestDeviceCode]);

    // 메시지 핸들러 - 대기 상태
    const handleWaitingForProducts = useCallback((payloadData) => {
        const currentDeviceCode = getCurrentDeviceCode();
        if (currentDeviceCode === payloadData.deviceCode) {
            NiceModal.show("waitingForProducts", {
                data: payloadData.userId,
                storeCode: payloadData.roomId
            });
        }
    }, [getCurrentDeviceCode]);

    // 메시지 핸들러 - 작동 중지
    const handleUnsetOperating = useCallback((payloadData) => {
        const currentDeviceCode = getCurrentDeviceCode();
        if (currentDeviceCode === payloadData.deviceCode) {
            NiceModal.remove("itemDispensing");
        } else {
            NiceModal.remove("waitForItemCollection");
        }
    }, [getCurrentDeviceCode]);

    // 메시지 핸들러 생성 함수
    const createMessageHandlers = useCallback((navigate, id) => {
        return {
            executeDispense: handleExecuteDispense,
            updateRailStatus: handleUpdateRailStatus,
            railCompletionConfirmed: handleRailCompletionConfirmed,
            order: (payloadData) => handleOrder(payloadData, navigate, id),
            dispenseError: handleDispenseError,
            dispenseTimeout: handleDispenseTimeout,
            test: () => alert("ws test!!"),
            waitingForProducts: handleWaitingForProducts,
            unsetOperating: handleUnsetOperating,
            addCart: (payloadData) => {
                //console.log("[Dispense-Handler] addCart 핸들러 호출됨:", payloadData);
                logEvent("장바구니 추가", JSON.stringify(payloadData));
                const deviceCode = getCurrentDeviceCode();
                //console.log("[Dispense-Handler] 현재 디바이스:", deviceCode);

                // 디바이스 코드가 null인 경우에 대한 방어 코드 추가
                if (!deviceCode) {
                    logEvent("장바구니 추가 실패", "디바이스 코드를 가져올 수 없음");
                    return;
                }

                if (Array.isArray(payloadData.cartList)) {
                    payloadData.cartList.forEach(data => {
                        // 명확한 디바이스 코드 비교
                        if (deviceCode === payloadData.deviceCode) {
                            //console.log("[Dispense-Handler] 내 장바구니에 추가:", data);
                            // 로깅 추가로 디버깅 용이하게
                            logEvent("내 장바구니에 추가", `상품: ${data.productName}, ID: ${data.id}`);
                            dispatch(CartReducer.actions.addProductToCart({
                                ...data,
                                deviceCode: payloadData.deviceCode
                            }));
                        } else {
                            //console.log("[Dispense-Handler] 다른 디바이스 장바구니에 추가됨:", payloadData.deviceCode);
                            logEvent("다른 디바이스 장바구니에 추가됨", `디바이스: ${payloadData.deviceCode}`);
                            dispatch(CartReducer.actions.setRemainProducts({
                                id: data.id,
                                productName: data.productName,
                                quantity: data.quantity
                            }));
                        }
                    });
                } else {
                    //console.log("[Dispense-Handler] cartList 형식 오류:", payloadData);
                    logEvent("장바구니 추가 실패", "cartList가 배열이 아님");
                }
            },
            demoAddCart: (payloadData) => {
                logEvent("데모 장바구니 추가", JSON.stringify(payloadData));
                payloadData.cartList.forEach(data => {
                    dispatch(CartReducer.actions.addProductToCart({
                        ...data,
                        deviceCode: payloadData.deviceCode
                    }));
                });
            },
            updateCartQuantity: (payloadData) => {
                const deviceCode = getCurrentDeviceCode();
                payloadData.cartList.forEach(data => {
                    if (deviceCode === payloadData.deviceCode) {
                        dispatch(CartReducer.actions.updateQuantity({
                            ...data,
                            deviceCode: payloadData.deviceCode
                        }));
                    } else {
                        dispatch(CartReducer.actions.updateRemainQuantity({
                            id: data.id,
                            productName: data.productName,
                            quantity: data.quantity
                        }));
                    }
                });
            },
            removeProductFromCart: (payloadData) => {
                const deviceCode = getCurrentDeviceCode();
                payloadData.cartList.forEach(data => {
                    if (deviceCode === payloadData.deviceCode) {
                        dispatch(CartReducer.actions.removeProductFromCart({ ...data }));
                    } else {
                        dispatch(CartReducer.actions.removeRemainProductsFromCart({
                            id: data.id
                        }));
                    }
                });
            },
            clearCartProducts: (payloadData) => {
                const deviceCode = getCurrentDeviceCode();
                if (deviceCode === payloadData.deviceCode) {
                    dispatch(CartReducer.actions.reset());
                } else {
                    dispatch(CartReducer.actions.clearRemainProducts());
                }
            },
            forceSendout: (payloadData) => {
                try {
                    const jsonString = JSON.stringify(payloadData.content);
                    const base64EncodedString = btoa(jsonString);
                    // 기존 sendOut 대신 executeSendOut 사용
                    if (window.BRIDGE?.executeSendOut) {
                        window.BRIDGE.executeSendOut(base64EncodedString, null);
                    } else if (window.BRIDGE?.sendOut) {
                        // 하위 호환성을 위한 fallback
                        window.BRIDGE.sendOut(base64EncodedString);
                    }
                } catch (error) {
                    //console.error('forceSendout 실행 중 오류:', error);
                }
            }
        };
    }, [
        handleExecuteDispense,
        handleUpdateRailStatus,
        handleRailCompletionConfirmed,
        handlePartialRailCompletion,
        handleOrder,
        handleDispenseError,
        handleDispenseTimeout,
        handleWaitingForProducts,
        handleUnsetOperating,
        dispatch,
        getCurrentDeviceCode,
        logEvent
    ]);

    // 웹소켓 메시지 핸들링 함수
    const handleWebSocketMessage = useCallback((payload, navigate, id) => {
        try {
            const payloadData = JSON.parse(payload.body);
            const handlers = createMessageHandlers(navigate, id);
            const handler = handlers[payloadData.code];

            if (handler) {
                handler(payloadData);
            } else {
                logEvent("알 수 없는 메시지", `알 수 없는 메시지 타입: ${payloadData.code}`);
            }
        } catch (error) {
            logEvent("웹소켓 메시지 처리 중 오류", error.message);
        }
    }, [createMessageHandlers, logEvent]);

    // 마지막 dispense 요청 재전송 (필요시 사용)
    const resendLastDispenseRequest = useCallback((roomId) => {
        const lastRequest = lastDispenseRequestRef.current;
        if (!lastRequest) {
            logEvent("재전송 실패", "마지막 배출 요청 정보가 없음");
            return false;
        }

        logEvent("마지막 배출 요청 재전송", `디바이스: ${lastRequest.deviceCode}`);

        const dispenseMessage = {
            code: "dispense",
            roomId: roomId,
            content: lastRequest.content,
            deviceCode: lastRequest.deviceCode,
            orderTimestamp: Date.now(),
            orderKey: lastRequest.orderKey || generateOrderKey(),
            isRetry: true
        };

        return sendMessageFn(dispenseMessage);
    }, [logEvent, sendMessageFn, generateOrderKey]);

    return {
        isRailActive,
        activeDevice,
        requestDeviceCode,
        activeOutlets,
        handleWebSocketMessage,
        notifyRailCompleted: () => notifyRailCompletedRef.current && notifyRailCompletedRef.current(),
        setActiveOutlets,
        resendLastDispenseRequest,
        currentOrderKey: orderKeyRef.current,
        generateOrderKey
    };
}